import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  Grid,
  Stack,
  Typography,
  Chip,
  Button,
  useTheme,
  Tooltip,
  Collapse,
  IconButton
} from '@mui/material';
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/CancelOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import KeyIcon from '@mui/icons-material/VpnKey';
import BlockIcon from '@mui/icons-material/Block';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InvoiceDetailsCardStyles from '../../../styles/purchaseInvoices/invoiceManagement/InvoiceDetailsCardStyles';
import { getStatusColor } from '../../../Utils/purchaseInvoices/invoiceManagement/invoiceUtils';
import { translateStatus } from '../../../Utils/statusTranslations';
import { softColors } from '../../../styles/commonColors';

const InvoiceDetailsCard = ({ invoice, approvalPackageData, onNavigateToFile, onNavigateToComparison, getAccountantDisplayName, accountantNamesCache }) => {
  const theme = useTheme();

  const [showAutoApprovalDetails, setShowAutoApprovalDetails] = useState(false);

  const accountantDisplayName = useMemo(() => {
    const accountManager = invoice?.approvalPackage?.accountManager || approvalPackageData?.accountManager;
    return getAccountantDisplayName ? getAccountantDisplayName(accountManager) : (accountManager?.name || accountManager || null);
  }, [invoice?.approvalPackage?.accountManager, approvalPackageData?.accountManager, accountantNamesCache, getAccountantDisplayName]);

  const parseAutoApprovalReason = (reason, details) => {
    if (!reason) return null;

    const checks = [];

    const priceMatch = reason.match(/התאמה מלאה בין סכום החשבונית להזמנה \(הפרש: ([\d.]+) ₪\)/);
    if (priceMatch) {
      checks.push({
        type: 'price',
        title: 'בדיקת התאמת מחירים',
        icon: <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />,
        details: (details?.priceCheck ? (
          <>
            <Typography variant="body2" sx={{ color: '#2E7D32', fontSize: '0.813rem', mb: 0.5 }}>
              • חשבונית: {Number(details.priceCheck.invoiceTotal).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₪
            </Typography>
            <Typography variant="body2" sx={{ color: '#2E7D32', fontSize: '0.813rem', mb: 0.5 }}>
              • הזמנה: {Number(details.priceCheck.orderTotal).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₪
            </Typography>
            <Typography variant="body2" sx={{ color: '#2E7D32', fontSize: '0.813rem' }}>
              • הפרש: {Number(details.priceCheck.difference).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₪
            </Typography>
          </>
        ) : `הפרש: ${priceMatch[1]} ₪ (בתוך סף 1 ₪)`)
      });
    }

    const aiMatch = reason.match(/שיוך שורות AI מעל הסף \(ממוצע: ([\d.]+)%\)/);
    if (aiMatch) {
      checks.push({
        type: 'ai',
        title: 'שיוך אוטומטי של שורות',
        icon: <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />,
        details: details?.aiMapping ? (
          <>
            <Typography variant="body2" sx={{ color: '#2E7D32', fontSize: '0.813rem', mb: 0.3 }}>
              • רמת התאמה ממוצעת: {details.aiMapping.avgConfidence.toFixed(1)}%
            </Typography>
            <Typography variant="body2" sx={{ color: '#2E7D32', fontSize: '0.813rem', mb: 0.3 }}>
              • {details.aiMapping.totalLines} שורות מופו בהצלחה
            </Typography>
            <Typography variant="body2" sx={{ color: '#2E7D32', fontSize: '0.813rem' }}>
              • התאמה מינימלית: {details.aiMapping.minConfidence.toFixed(1)}%
            </Typography>
          </>
        ) : `רמת התאמה ממוצעת: ${aiMatch[1]}%`
      });
    }

    if (checks.length === 0) {
      checks.push({
        type: 'general',
        title: 'אישור אוטומטי',
        icon: <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />,
        details: reason
      });
    }

    return checks;
  };

  const autoApprovalChecks = useMemo(() => {
    if (invoice.autoApproved && invoice.autoApprovalReason) {
      return parseAutoApprovalReason(
        invoice.autoApprovalReason,
        invoice.autoApprovalDetails 
      );
    }
    return null;
  }, [invoice.autoApproved, invoice.autoApprovalReason, invoice.autoApprovalDetails]);

  const getAllocationInfo = useMemo(() => {
    const approvalPackage = approvalPackageData || invoice?.approvalPackage;

    if (!approvalPackage) return null;
    const allocationKey = approvalPackage.allocationKey;
    const percentageAllocations = approvalPackage.percentageAllocations;
    const allocationType = approvalPackage.allocationType;

    if (allocationType === 'percentage_allocation' && percentageAllocations?.length > 0) {
      return {
        type: 'percentage',
        icon: <BusinessIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.success.main }} />,
        title: 'חלוקה לחברות',
        details: percentageAllocations.map(allocation =>
          `${allocation.companyName}: ${allocation.quantity ? allocation.quantity + 'ש' : ''}(${allocation.percentage}%)`
        ).join(' • '),
        allocations: percentageAllocations
      };
    }

    if (allocationKey?.name && allocationKey.name !== 'NO_KEY') {
      const displayName = allocationKey.name || allocationKey.splitKeyName;
      return {
        type: 'allocation_key',
        icon: <KeyIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.info.main }} />,
        title: 'מפתח העמסה',
        details: displayName
      };
    }

    if (allocationKey?.splitKeyName && allocationKey.splitKeyName !== 'NO_KEY' && !allocationKey.name) {
      return {
        type: 'allocation_key',
        icon: <KeyIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.info.main }} />,
        title: 'מפתח העמסה',
        details: allocationKey.splitKeyName
      };
    }

    if (allocationKey?.name === 'NO_KEY' || allocationKey?.isNoKey || allocationType === 'no_allocation_key') {
      return {
        type: 'no_key',
        icon: <BlockIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.warning.main }} />,
        title: 'ללא מפתח העמסה',
        details: 'החשבונית אושרה ללא מפתח העמסה'
      };
    }

    return null;
  }, [approvalPackageData, invoice?.approvalPackage, theme]);

  return (
    <Box sx={InvoiceDetailsCardStyles.container}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={InvoiceDetailsCardStyles.card}>
            <Box sx={InvoiceDetailsCardStyles.sideMarker(theme.palette.primary.main)} />

            <Typography variant="h6" gutterBottom color="primary" fontWeight="medium">
              פרטי החשבונית
            </Typography>

            <Stack
              spacing={3}
              sx={InvoiceDetailsCardStyles.stackContainer(theme)}
            >
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  מספר חשבונית
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {invoice?.booknum || invoice?.id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  ספק
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {invoice?.supplierName}
                </Typography>
              </Box>
              {invoice?.supNumber && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    מספר ספק
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {invoice.supNumber}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  תאריך החשבונית
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {invoice?.ivDate ? invoice.ivDate.replace(/\./g, '/') : (invoice?.date ? invoice.date.replace(/\./g, '/') : '-')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  סטטוס
                </Typography>
                <Chip
                  label={translateStatus(invoice?.status)}
                  color={['final', 'retainer', 'clarification'].includes(getStatusColor(invoice?.status)) ? undefined : getStatusColor(invoice?.status)}
                  size="small"
                  sx={{
                    ...InvoiceDetailsCardStyles.statusChip,
                    ...(getStatusColor(invoice?.status) === 'final' && {
                      backgroundColor: softColors.final,
                      color: '#fff',
                      border: `1px solid ${softColors.final}`
                    }),
                    ...(getStatusColor(invoice?.status) === 'retainer' && {
                      backgroundColor: '#F3E5F5',
                      color: '#9C27B0',
                      border: `1px solid #CE93D8`,
                      fontWeight: 'bold'
                    }),
                    ...(getStatusColor(invoice?.status) === 'clarification' && {
                      backgroundColor: '#F3E5F5',
                      color: '#7B1FA2',
                      border: `1px solid #CE93D8`,
                      fontWeight: 'bold'
                    })
                  }}
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  קובץ
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    sx={InvoiceDetailsCardStyles.fileLink(theme)}
                    onClick={onNavigateToFile}
                  >
                    {invoice?.fileName}
                  </Typography>
                </Box>
              </Box>
              {invoice?.groupSettings?.name && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    קבוצה
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {invoice.groupSettings.name}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={InvoiceDetailsCardStyles.card}>
            <Box sx={InvoiceDetailsCardStyles.sideMarker(theme.palette.secondary.main)} />
            <Typography variant="h6" gutterBottom color="primary" fontWeight="medium">
              מידע נוסף
            </Typography>
            <Stack
              spacing={3}
              sx={InvoiceDetailsCardStyles.stackContainer(theme)}
            >
              {invoice?.companyName && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    שם החברה
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {invoice.companyName}
                  </Typography>
                </Box>
              )}

              {(invoice?.approvalPackage?.accountManager || approvalPackageData?.accountManager) && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    מנהלת חשבונות
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {accountantDisplayName}
                  </Typography>
                </Box>
              )}
              {getAllocationInfo && (invoice?.status === 'מאושר' || invoice?.status === 'אושר' || approvalPackageData) && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    אופן האישור
                  </Typography>

                  {getAllocationInfo.type === 'percentage' && getAllocationInfo.allocations ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {getAllocationInfo.allocations.map((allocation, index) => (
                        <Chip
                          key={index}
                          label={`${allocation.companyName} • ${allocation.quantity ? allocation.quantity + 'ש׳ • ' : ''}${allocation.percentage}%`}
                          color="success"
                          variant="outlined"
                          size="small"
                          icon={<BusinessIcon />}
                          sx={{
                            bgcolor: 'success.50',
                            borderColor: 'success.200',
                            color: 'success.dark',
                            fontWeight: 'medium',
                            '& .MuiChip-icon': {
                              color: 'success.main'
                            }
                          }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Chip
                      label={getAllocationInfo.details}
                      color={getAllocationInfo.type === 'allocation_key' ? 'info' : 'warning'}
                      variant="outlined"
                      size="small"
                      icon={getAllocationInfo.type === 'allocation_key' ? <KeyIcon /> : <BlockIcon />}
                      sx={{
                        bgcolor: getAllocationInfo.type === 'allocation_key' ? 'info.50' : 'warning.50',
                        borderColor: getAllocationInfo.type === 'allocation_key' ? 'info.200' : 'warning.200',
                        color: getAllocationInfo.type === 'allocation_key' ? 'info.dark' : 'warning.dark',
                        fontWeight: 'medium',
                        '& .MuiChip-icon': {
                          color: getAllocationInfo.type === 'allocation_key' ? 'info.main' : 'warning.main'
                        }
                      }}
                    />
                  )}
                </Box>
              )}

              {invoice?.approver && !invoice?.approvedBy && !invoice?.rejectedBy && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    מאשר
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {invoice.approver}
                  </Typography>
                </Box>
              )}

              {(invoice?.approvedBy && invoice?.approvedAt) && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 'bold' }}>
                      מאושר על ידי {invoice.approvedBy}
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    תאריך ושעה:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {new Date(invoice.approvedAt).toLocaleString('he-IL', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>

                  {invoice.autoApproved && invoice.autoApprovalReason && autoApprovalChecks && (
                    <Box sx={{
                      mt: 2,
                      backgroundColor: '#E8F5E9',
                      borderRadius: 2,
                      border: '1px solid #66BB6A',
                      overflow: 'hidden'
                    }}>
                      <Box sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#DFF0D8'
                        }
                      }}
                        onClick={() => setShowAutoApprovalDetails(!showAutoApprovalDetails)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                          <AutorenewIcon sx={{ color: '#2E7D32', fontSize: 24, flexShrink: 0 }} />
                          <Box>
                            <Typography variant="subtitle2" sx={{ 
                              fontWeight: 'bold', 
                              color: '#2E7D32',
                              mb: 0.5
                            }}>
                              ✅ אושר אוטומטית
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              color: '#1B5E20',
                              fontSize: '0.875rem'
                            }}>
                              {invoice.autoApprovalReason}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <IconButton 
                          size="small"
                          sx={{ 
                            color: '#2E7D32',
                            '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.1)' }
                          }}
                        >
                          {showAutoApprovalDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>

                      <Collapse in={showAutoApprovalDetails}>
                        <Box sx={{
                          px: 2,
                          pb: 2,
                          pt: 1,
                          borderTop: '1px solid #A5D6A7'
                        }}>
                          <Typography variant="caption" sx={{ 
                            color: '#2E7D32',
                            fontWeight: 'bold',
                            mb: 1.5,
                            display: 'block'
                          }}>
                            פרטי בדיקות האישור האוטומטי:
                          </Typography>
                          
                          <Stack spacing={1.5}>
                            {autoApprovalChecks.map((check, index) => (
                              <Box 
                                key={index}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 1.5,
                                  p: 1.5,
                                  backgroundColor: '#F1F8F4',
                                  borderRadius: 1,
                                  border: '1px solid #C8E6C9'
                                }}
                              >
                                {check.icon}
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ 
                                    fontWeight: 'bold',
                                    color: '#1B5E20',
                                    mb: 0.5
                                  }}>
                                    {check.title}
                                  </Typography>
                                  <Typography variant="body2" sx={{ 
                                    color: '#2E7D32',
                                    fontSize: '0.813rem'
                                  }}>
                                    {check.details}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Stack>

                          <Typography variant="caption" sx={{ 
                            display: 'block',
                            mt: 2,
                            color: '#558B2F',
                            fontStyle: 'italic'
                          }}>
                            💡 החשבונית אושרה אוטומטית לאחר שעברה את כל הבדיקות הנדרשות
                          </Typography>
                        </Box>
                      </Collapse>
                    </Box>
                  )}
                </Box>
              )}

              {(invoice?.rejectedBy && invoice?.rejectedAt) && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CloseIcon color="error" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 'bold' }}>
                      נדחה על ידי {invoice.rejectedBy}
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    תאריך ושעה:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {new Date(invoice.rejectedAt).toLocaleString('he-IL', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                  {(invoice.rejectionReason || invoice?.rejection?.reason) && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        סיבת דחייה:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          p: 1.5,
                          bgcolor: 'error.lighter',
                          color: 'error.dark',
                          borderRadius: 1,
                          fontWeight: 'medium'
                        }}
                      >
                        {invoice.rejectionReason || invoice?.rejection?.reason}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  תאריך קליטה במערכת
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {invoice?.uploadedAt ?
                    new Date(invoice.uploadedAt).toLocaleDateString('he-IL', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    }).replace(/\./g, '/') :
                    (invoice?.ivDate ? invoice.ivDate.replace(/\./g, '/') : (invoice?.date ? invoice.date.replace(/\./g, '/') : 'לא זמין'))
                  }
                </Typography>
              </Box>

              {invoice?.status === 'ממתין לאישור' && (
                <Box sx={InvoiceDetailsCardStyles.warningBox}>
                  <Typography variant="body2" fontWeight="medium" color="warning.dark">
                    חשבונית זו ממתינה לאישור
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    ניתן לצפות בהשוואת נתונים מול ההזמנה בטאב
                    <Button
                      variant="text"
                      sx={InvoiceDetailsCardStyles.linkButton}
                      onClick={onNavigateToComparison}
                    >
                      "השוואת פרטי הזמנה וחשבונית"
                    </Button>
                  </Typography>
                </Box>
              )}
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InvoiceDetailsCard;